import React, { useState, useEffect } from 'react';
import { Card, Input, Select, Textarea, FormGrid, Btn, Modal, useToast, DatePicker, Spinner, SlowRequestHint, ReturnDestinationNote } from '../UI';
import * as api from '../../api';
import { belongsInMyDrafts, errorText, runChecks, missingFieldsMessage, isAwaitingCorrection, correctionTarget, OTHER_OPTION, withOtherOption, otherAwareId, otherAwareText, otherAwareValue, bypassesDepartmentReview} from '../../utils';
import { RemoveLineButton } from './RequisitionLineItemsTable';
import MyInbox from './MyInbox';
import MyRequisitions from './MyRequisitions';
import RequestedForField from './RequestedForField';
import { useUOM } from '../../hooks/useUOM';

// ─── constants ──────────────────────────────────────────────────────────────

const CALC_MODES = [
  { value: 'standard', label: 'Standard Quantity × Unit Price' },
  { value: 'employees_days_daily', label: 'Employees × Days × Daily Rate' },
  { value: 'employees_hours_hourly', label: 'Employees × Hours × Hourly Rate' },
  { value: 'non_employee_days_daily', label: 'Non-Employee × Days × Daily Rate' },
  { value: 'fixed_amount', label: 'Fixed Amount' },
  { value: 'bulk_equal_split', label: 'Bulk Employee Equal Split' },
  { value: 'custom_amount', label: 'Custom Amount' },
];

const fmt = (v) => (v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '0.00');
const num = (v) => (parseFloat(v) || 0);
// What a number box holds, as the server takes it: a cleared box is an empty
// string, which the API rejects as "not a valid decimal" — send null instead.
// purchase_requests.title and purchase_request_items.description are capped at this on the server
const TEXT_LIMIT = 300;

const numOrNull = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const emptyItem = () => ({
  item_id: null,
  ledger_id: '',
  ledger_other: '',
  expense_category_id: '',
  expense_category_other: '',
  description: '',
  specification: '',
  employee_user_ids: [],
  non_employees: [],
  other_employees: [],
  vendor_id: '',
  vendor_other: '',
  item_requested_for_user_id: null,
  item_requested_for_name: null,
  project_id: '',
  quantity: 1,
  unit_of_measure: 'Day',
  estimated_unit_rate: 0,
  vat_percentage: 0,
  calculation_mode: 'standard',
  calc_days: 0,
  calc_daily_rate: 0,
  calc_hours: 0,
  calc_hourly_rate: 0,
  calc_fixed_amount: 0,
  calc_bulk_total: 0,
  calc_custom_amount: 0,
  remarks: '',
  showCalc: false,
});

// Non-employee row amount: whichever of days/hours-based figures are filled in.
const nonEmployeeAmount = (ne) => num(ne.days_worked) * num(ne.daily_rate) + num(ne.hours_worked) * num(ne.hourly_rate);

// "Non-Employee" and "Other Employees" are two headings over one kind of row:
// somebody paid through the requisition who is not on the employee list. They
// carry identical fields and persist to the same columns — the category is the
// only thing that separates them, and it exists so the split survives a save.
// A row saved before the split has no category, and can only have been a
// non-employee, so that is what NULL reads as.
const NON_EMPLOYEE = 'non_employee';
const OTHER_EMPLOYEE = 'other_employee';

// Every ad-hoc row on a line, whichever list it came from — for the figures
// that treat them alike (line amount, day and hour totals).
const adHocRows = (item) => [...(item.non_employees || []), ...(item.other_employees || [])];

function computeItemAmount(item) {
  const empCount = item.employee_user_ids?.length || 0;
  switch (item.calculation_mode) {
    case 'employees_days_daily':
      return empCount * num(item.calc_days) * num(item.calc_daily_rate);
    case 'employees_hours_hourly':
      return empCount * num(item.calc_hours) * num(item.calc_hourly_rate);
    case 'non_employee_days_daily':
      return adHocRows(item).reduce((sum, ne) => sum + nonEmployeeAmount(ne), 0);
    case 'fixed_amount':
      return num(item.calc_fixed_amount);
    case 'bulk_equal_split':
      return num(item.calc_bulk_total);
    case 'custom_amount':
      return num(item.calc_custom_amount);
    case 'standard':
    default:
      return num(item.quantity) * num(item.estimated_unit_rate);
  }
}

// VAT on one line. Whatever the calculation mode worked the line out to, the
// tax is a straight percentage of it — rounded to the kobo per line, exactly
// as the server rounds it, so the two never differ by a rounding step.
function computeItemVat(item) {
  return Math.round(computeItemAmount(item) * (num(item.vat_percentage) / 100) * 100) / 100;
}

function computeFiguresLabel(item) {
  const empCount = item.employee_user_ids?.length || 0;
  switch (item.calculation_mode) {
    case 'employees_days_daily':
      return `${empCount} Employee(s) × ${item.calc_days || 0} Days × ₦${fmt(item.calc_daily_rate)}`;
    case 'employees_hours_hourly':
      return `${empCount} Employee(s) × ${item.calc_hours || 0} Hours × ₦${fmt(item.calc_hourly_rate)}`;
    case 'non_employee_days_daily': {
      const nonEmp = (item.non_employees || []).length;
      const other = (item.other_employees || []).length;
      const parts = [];
      if (nonEmp) parts.push(`${nonEmp} Non-Employee(s)`);
      if (other) parts.push(`${other} Other Employee(s)`);
      return `${parts.join(' + ') || '0 Non-Employee(s)'} × Days/Hours × Rate`;
    }
    case 'fixed_amount':
      return 'Fixed Amount';
    case 'bulk_equal_split':
      return `₦${fmt(item.calc_bulk_total)} split equally among ${empCount} employee(s)`;
    case 'custom_amount':
      return 'Custom Amount';
    case 'standard':
    default:
      return `${item.quantity || 0} × ₦${fmt(item.estimated_unit_rate)}`;
  }
}

// ─── Employee Selector modal ────────────────────────────────────────────────
function EmployeeSelectorModal({ users, departments, initialSelectedIds, onCancel, onApply }) {
  const [selectedIds, setSelectedIds] = useState(initialSelectedIds || []);
  const [search, setSearch] = useState({ employeeId: '', name: '', departmentId: '', grade: '' });

  const toggle = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const filtered = users.filter((u) => {
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
    if (search.employeeId && !(u.employee_id || '').toLowerCase().includes(search.employeeId.toLowerCase())) return false;
    if (search.name && !fullName.includes(search.name.toLowerCase())) return false;
    if (search.departmentId && !(u.departments || []).some((d) => (d.id || d.department_id) === search.departmentId)) return false;
    if (search.grade && !(u.grade || '').toLowerCase().includes(search.grade.toLowerCase())) return false;
    return true;
  });

  return (
    <Modal title="Employee Selector" onClose={onCancel} width={900} headerBg="#0b318bff" headerColor="#ffffff">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Search by ID, name, department or grade · multi-select</div>
        <FormGrid cols={4}>
          <Input placeholder="Employee ID" value={search.employeeId} onChange={(v) => setSearch((s) => ({ ...s, employeeId: v }))} />
          <Input placeholder="Full Name" value={search.name} onChange={(v) => setSearch((s) => ({ ...s, name: v }))} />
          <Select
            value={search.departmentId}
            onChange={(v) => setSearch((s) => ({ ...s, departmentId: v }))}
            options={[{ value: '', label: 'All Departments' }, ...departments.map((d) => ({ value: d.id || d.department_id, label: d.name }))]}
          />
          <Input placeholder="Grade" value={search.grade} onChange={(v) => setSearch((s) => ({ ...s, grade: v }))} />
        </FormGrid>

        <div style={{ maxHeight: '45vh', overflowY: 'auto', border: '1px solid black', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff', position: 'sticky', top: 0 }}>
                <th style={{ padding: '8px 10px', width: 34 }}></th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>EMP ID</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>FULL NAME</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>DEPARTMENT</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>JOB TITLE</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>GRADE</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>BRANCH</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const id = u.user_id || u.id;
                const checked = selectedIds.includes(id);
                return (
                  <tr
                    key={id}
                    onClick={() => toggle(id)}
                    style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: checked ? '#e0e7ff' : '#fff' }}
                  >
                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(id)} onClick={(e) => e.stopPropagation()} />
                    </td>
                    <td style={{ padding: '8px 10px' }}>{u.employee_id || '—'}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{u.first_name} {u.last_name}</td>
                    <td style={{ padding: '8px 10px' }}>{(u.departments || []).map((d) => d.name).join(', ') || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{u.job_title || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{u.grade || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{u.branch || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{u.is_active ? 'Active' : 'Inactive'}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No employees found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{selectedIds.length} employee(s) selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
            <Btn onClick={() => onApply(selectedIds)} style={{ background: '#0b318bff', color: '#fff', border: 'none' }}>Apply Selection</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Ad-hoc personnel modal ─────────────────────────────────────────────────
// Serves both "Non-Employee" and "Other Employees". The two lists hold the
// same fields and differ only in what they are called, so the grid below is
// shared and the wording is passed in.
function NonEmployeeModal({ initialRows, onCancel, onSave, title, hint, saveLabel }) {
  const emptyRow = () => ({ full_name: '', designation: '', phone: '', days_worked: 1, hours_worked: 0, daily_rate: 0, hourly_rate: 0, remarks: '' });
  const [rows, setRows] = useState(initialRows && initialRows.length > 0 ? initialRows : [emptyRow()]);

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const total = rows.reduce((sum, r) => sum + nonEmployeeAmount(r), 0);

  return (
    <Modal title={title} onClose={onCancel} width={950} headerBg="#0b318bff" headerColor="#ffffff">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{hint}</div>
        <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 850 }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>FULL NAME</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>ROLE / DESIGNATION</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>PHONE</th>
                <th style={{ padding: '8px 10px', textAlign: 'center' }}>DAYS</th>
                <th style={{ padding: '8px 10px', textAlign: 'center' }}>HOURS</th>
                <th style={{ padding: '8px 10px', textAlign: 'center' }}>DAILY RATE</th>
                <th style={{ padding: '8px 10px', textAlign: 'center' }}>HOURLY RATE</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>AMOUNT</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>REMARKS</th>
                <th style={{ padding: '8px 10px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 6 }}><input style={{ width: 130, padding: 6 }} value={r.full_name} onChange={(e) => updateRow(idx, 'full_name', e.target.value)} placeholder="Full name" /></td>
                  <td style={{ padding: 6 }}><input style={{ width: 110, padding: 6 }} value={r.designation} onChange={(e) => updateRow(idx, 'designation', e.target.value)} placeholder="Role" /></td>
                  <td style={{ padding: 6 }}><input style={{ width: 100, padding: 6 }} value={r.phone} onChange={(e) => updateRow(idx, 'phone', e.target.value)} placeholder="Phone" /></td>
                  <td style={{ padding: 6 }}><input type="number" style={{ width: 60, padding: 6 }} value={r.days_worked} onChange={(e) => updateRow(idx, 'days_worked', e.target.value)} /></td>
                  <td style={{ padding: 6 }}><input type="number" style={{ width: 60, padding: 6 }} value={r.hours_worked} onChange={(e) => updateRow(idx, 'hours_worked', e.target.value)} /></td>
                  <td style={{ padding: 6 }}><input type="number" style={{ width: 80, padding: 6 }} value={r.daily_rate} onChange={(e) => updateRow(idx, 'daily_rate', e.target.value)} /></td>
                  <td style={{ padding: 6 }}><input type="number" style={{ width: 80, padding: 6 }} value={r.hourly_rate} onChange={(e) => updateRow(idx, 'hourly_rate', e.target.value)} /></td>
                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>₦{fmt(nonEmployeeAmount(r))}</td>
                  <td style={{ padding: 6 }}><input style={{ width: 100, padding: 6 }} value={r.remarks} onChange={(e) => updateRow(idx, 'remarks', e.target.value)} placeholder="Remarks" /></td>
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    <button type="button" onClick={() => removeRow(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 900 }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Btn variant="outline" onClick={addRow}>+ Add Row</Btn>
          <div style={{ fontWeight: 800 }}>Total: ₦{fmt(total)}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
          <Btn onClick={() => onSave(rows.filter((r) => r.full_name))} style={{ background: '#0b318bff', color: '#fff', border: 'none' }}>{saveLabel}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function HRRequisition({ onBack, onSaveSuccess, editPrId, department, subView, onViewChange }) {
  const todayDate = new Date().toISOString().split('T')[0];
  const { show: showToast } = useToast();
  const { uomOptions: UOM_OPTIONS } = useUOM();

  // 'form' | 'review' | 'drafts'
  //
  // Controlled when the caller passes subView, uncontrolled otherwise. Both are
  // aliased to the same names so every reference below is untouched — there is
  // no second code path to keep in step, and callers that pass nothing behave
  // exactly as they did before.
  const [viewState, setViewState] = useState('form');
  const view = subView || viewState;
  const setView = onViewChange || setViewState;
  const [reviewCount, setReviewCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  // True while My Drafts has a requisition open for correction — see the
  // drafts view below.
  const [editingDraft, setEditingDraft] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Populated when editing an existing PR, so a corrected RETURNED request can
  // be resubmitted straight back to the step that returned it. Without these
  // this form could save the corrections but never send them onward — the
  // edits looked saved and simply never moved.
  const [editWorkflowInstanceId, setEditWorkflowInstanceId] = useState(null);
  // Where Save & Resubmit will send this request. Read off the loaded
  // request rather than fetched, and shown in the confirm dialog so this
  // route names its destination like the inbox and details page do.
  const [resubmitTarget, setResubmitTarget] = useState('');
  const [resubmitComment, setResubmitComment] = useState('');
  const [editPrStatus, setEditPrStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // Their requisitions skip Department Head Review, so the button must not
  // promise a review that will not happen, and afterwards they belong in My
  // Requisitions rather than a department queue they never enter.
  const skipsDepartmentReview = bypassesDepartmentReview(currentUser);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseRequestTypes, setPurchaseRequestTypes] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    pr_number: '',
    title: '',
    description: '',
    requested_for: '',
    requested_department_id: department?.id || department?.department_id || '',
    purchase_request_type_id: '',
    purchase_request_type_other: '',
    priority_status: 'normal',
    required_date: todayDate,
    expected_due_date: todayDate,
    remarks: '',
    adjustments_amount: 0,
    adjustment_reason: '',
    require_vendor: false,
  });

  const [items, setItems] = useState([emptyItem()]);

  const [employeeSelectorForLine, setEmployeeSelectorForLine] = useState(null); // line index
  const [nonEmployeeModalForLine, setNonEmployeeModalForLine] = useState(null); // line index
  const [otherEmployeeModalForLine, setOtherEmployeeModalForLine] = useState(null); // line index

  const [confirmModal, setConfirmModal] = useState(null); // 'DISCARD' | 'DRAFT'
  const pendingStatusRef = React.useRef(null);
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [firstStepInfo, setFirstStepInfo] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedPrRef, setSubmittedPrRef] = useState('');

  const fetchReviewCount = async (deptId) => {
    if (!deptId) return;
    try {
      const res = await api.getDepartmentInbox(deptId, 'Department Head Review');
      const list = Array.isArray(res) ? res : (res?.data || []);
      setReviewCount(list.length);
    } catch (e) {
      // non-fatal — badge just stays at 0
    }
  };

  const fetchDraftCount = async (deptId) => {
    if (!deptId) return;
    try {
      const res = await api.listMyPRs();
      const list = Array.isArray(res) ? res : (res?.data || []);
      // Same predicate the My Drafts list uses, so the badge and the tab can
      // never disagree — a department-head bounce sits in drafts and must be
      // counted there too.
      const count = list.filter((pr) =>
        belongsInMyDrafts(pr) &&
        String(pr.requested_department_id || '') === String(deptId)
      ).length;
      setDraftCount(count);
    } catch (e) {
      // non-fatal — badge just stays at 0
    }
  };

  // After a brand-new (non-edit) draft save or submission, we land the user
  // on the My Drafts / Manager Review pill instead of navigating away — so
  // the form underneath needs to be blanked out itself, otherwise reopening
  // "New Requisition" would show the just-saved values instead of a blank form.
  const resetFormForNewEntry = async () => {
    const deptId = department?.id || department?.department_id || '';

    setFormData({
      pr_number: '',
      title: '',
      description: '',
      requested_for: '',
      requested_department_id: deptId,
      purchase_request_type_id: '',
    purchase_request_type_other: '',
      priority_status: 'normal',
      required_date: todayDate,
      expected_due_date: todayDate,
      remarks: '',
    adjustments_amount: 0,
      adjustment_reason: '',
      require_vendor: false,
    });
    setItems([emptyItem()]);
    setErrors({});
  };

  const handleSuccessModalClose = async () => {
    setShowSuccessModal(false);
    // A bypassing requester never enters this department's review queue, so
    // the pending-review view would be empty and their new requisition
    // nowhere in sight. Send them to My Requisitions, where it actually is.
    if (editPrId || skipsDepartmentReview) {
      onSaveSuccess?.();
    } else {
      const deptId = department?.id || department?.department_id;
      await resetFormForNewEntry();
      fetchReviewCount(deptId);
      setView('review');
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        api.clearApiCache();
        const [deptRes, projRes, suppRes, typeRes, ledgerRes, expRes, userRes, meRes] = await Promise.all([
          api.listDepartments(0, 100, true).catch(() => ({ departments: [] })),
          api.listProjects().catch(() => ({ data: [] })),
          api.listSuppliers().catch(() => ({ data: [] })),
          api.listPurchaseRequestTypes(true).catch(() => ({ data: [] })),
          api.listLedgers(true).catch(() => ({ ledgers: [] })),
          api.listExpenseCategories().catch(() => ({ data: [] })),
          api.listUsers(0, 500).catch(() => []),
          api.getMe().catch(() => null),
        ]);
        setDepartments(Array.isArray(deptRes) ? deptRes : (deptRes.departments || deptRes.data || []));
        setProjects(Array.isArray(projRes) ? projRes : (projRes.data || []));
        setSuppliers(Array.isArray(suppRes) ? suppRes : (suppRes.data || suppRes.items || []));
        setExpenseCategories(Array.isArray(expRes) ? expRes : (expRes.data || expRes.expense_categories || []));
        setPurchaseRequestTypes(Array.isArray(typeRes) ? typeRes : (typeRes.types || typeRes.data || []));
        setLedgers(Array.isArray(ledgerRes) ? ledgerRes : (ledgerRes.ledgers || ledgerRes.data || []));
        setUsers(Array.isArray(userRes) ? userRes : (userRes.data || []));
        setCurrentUser(meRes);
        if (!editPrId) {
          fetchReviewCount(department?.id || department?.department_id);
          fetchDraftCount(department?.id || department?.department_id);
        }

        if (editPrId) {
          const prRes = await api.getPR(editPrId);
          const pr = prRes?.data || prRes;
          setEditWorkflowInstanceId(pr.workflow_instance_id || null);
          setResubmitTarget(isAwaitingCorrection(pr) ? correctionTarget(pr) : '');
          setEditPrStatus((pr.request_status || '').toLowerCase());
          fetchReviewCount(pr.requested_department_id);
          fetchDraftCount(pr.requested_department_id);
          setFormData({
            pr_number: pr.pr_number || '',
            title: pr.title || '',
            description: pr.description || '',
            requested_for: pr.requested_for || '',
            requested_department_id: pr.requested_department_id || '',
            purchase_request_type_id: otherAwareValue(
              pr.purchase_request_type_id, pr.purchase_request_type_other),
            purchase_request_type_other: pr.purchase_request_type_other || '',
            priority_status: pr.priority_status || 'normal',
            required_date: pr.required_date || todayDate,
            expected_due_date: pr.expected_due_date || todayDate,
            remarks: pr.remarks || '',
            adjustments_amount: pr.adjustments_amount || 0,
            adjustment_reason: pr.adjustment_reason || '',
            require_vendor: pr.require_vendor || false,
          });
          const loadedItems = (pr.items && pr.items.length > 0) ? pr.items.map((item) => {
            const hrRows = item.hr_employees || [];
            const employeeRows = hrRows.filter((hr) => hr.employee_user_id);
            const adHoc = hrRows.filter((hr) => !hr.employee_user_id);
            // Anything saved before the split has no category and can only
            // have been a non-employee, so it loads back into that list.
            const nonEmployeeRows = adHoc.filter((hr) => hr.non_employee_category !== OTHER_EMPLOYEE);
            const otherEmployeeRows = adHoc.filter((hr) => hr.non_employee_category === OTHER_EMPLOYEE);
            const toAdHocRow = (hr) => ({
              full_name: hr.non_employee_full_name || '',
              designation: hr.non_employee_designation || '',
              phone: '',
              days_worked: hr.non_employee_days || 0,
              hours_worked: hr.non_employee_hours || 0,
              daily_rate: hr.non_employee_rate || 0,
              hourly_rate: 0,
              remarks: hr.non_employee_remarks || '',
            });
            const firstEmployeeRow = employeeRows[0];
            return {
              item_id: item.item_id || null,
              ledger_id: otherAwareValue(item.ledger_id, item.ledger_other),
              ledger_other: item.ledger_other || '',
              expense_category_id: otherAwareValue(item.expense_category_id, item.expense_category_other),
              expense_category_other: item.expense_category_other || '',
              description: item.description || '',
              specification: item.specification || '',
              employee_user_ids: employeeRows.map((hr) => hr.employee_user_id),
              non_employees: nonEmployeeRows.map(toAdHocRow),
              other_employees: otherEmployeeRows.map(toAdHocRow),
              vendor_id: otherAwareValue(item.vendor_id, item.vendor_other),
              vendor_other: item.vendor_other || '',
              item_requested_for_user_id: item.item_requested_for_user_id === OTHER_OPTION ? null : (item.item_requested_for_user_id || null),
              item_requested_for_name: item.item_requested_for_user_id === OTHER_OPTION ? (item.item_requested_for_name || null) : null,
              project_id: item.project_id || '',
              quantity: item.quantity || 1,
              unit_of_measure: item.unit_of_measure || 'Day',
              estimated_unit_rate: item.estimated_unit_rate || 0,
              vat_percentage: item.vat_percentage != null ? parseFloat(item.vat_percentage) : 0,
              calculation_mode: item.calculation_mode || 'standard',
              calc_days: firstEmployeeRow?.non_employee_days || 0,
              calc_daily_rate: firstEmployeeRow?.non_employee_rate || 0,
              calc_hours: firstEmployeeRow?.non_employee_hours || 0,
              calc_hourly_rate: firstEmployeeRow?.non_employee_rate || 0,
              calc_fixed_amount: item.calculation_mode === 'fixed_amount' ? (item.estimated_amount || 0) : 0,
              calc_bulk_total: item.calculation_mode === 'bulk_equal_split' ? (item.estimated_amount || 0) : 0,
              calc_custom_amount: item.calculation_mode === 'custom_amount' ? (item.estimated_amount || 0) : 0,
              remarks: item.remarks || '',
              showCalc: false,
            };
          }) : [emptyItem()];
          setItems(loadedItems);
        } else {

        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [editPrId]);

  // Re-check the My Drafts count whenever the user leaves that tab (e.g.
  // after submitting or deleting a draft there), so the badge doesn't go stale.
  useEffect(() => {
    if (view !== 'drafts') {
      fetchDraftCount(formData.requested_department_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ─── item helpers ─────────────────────────────────────────────────────────
  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  // ─── totals ───────────────────────────────────────────────────────────────
  const grossAmount = items.reduce((sum, it) => sum + computeItemAmount(it), 0);
  // VAT is carried by the individual lines, so the requisition's tax is the
  // sum of theirs. The adjustment is not taxed — it is a manual correction
  // with no rate of its own — so it joins the total untaxed. Same arithmetic
  // the server runs on what it is sent.
  const vatAmount = items.reduce((sum, it) => sum + computeItemVat(it), 0);
  const totalRequisitionValue = grossAmount + num(formData.adjustments_amount) + vatAmount;
  const employeeCount = new Set(items.flatMap((it) => it.employee_user_ids)).size;
  const nonEmployeeCount = items.reduce((sum, it) => sum + (it.non_employees?.length || 0), 0);
  const otherEmployeeCount = items.reduce((sum, it) => sum + (it.other_employees?.length || 0), 0);
  const totalDays = items.reduce((sum, it) => {
    if (it.calculation_mode === 'employees_days_daily') return sum + num(it.calc_days);
    return sum + adHocRows(it).reduce((s, ne) => s + num(ne.days_worked), 0);
  }, 0);
  const totalHours = items.reduce((sum, it) => {
    if (it.calculation_mode === 'employees_hours_hourly') return sum + num(it.calc_hours);
    return sum + adHocRows(it).reduce((s, ne) => s + num(ne.hours_worked), 0);
  }, 0);

  // ─── save / submit ────────────────────────────────────────────────────────
  const buildMappedItems = () => items.map((item) => {
    const amount = computeItemAmount(item);
    const empCount = item.employee_user_ids?.length || 0;
    const isEmployeeDaysMode = item.calculation_mode === 'employees_days_daily';
    const isEmployeeHoursMode = item.calculation_mode === 'employees_hours_hourly';

    const employeeHrRows = item.employee_user_ids.map((uid) => ({
      employee_user_id: uid,
      non_employee_days: isEmployeeDaysMode ? numOrNull(item.calc_days) : null,
      non_employee_hours: isEmployeeHoursMode ? numOrNull(item.calc_hours) : null,
      non_employee_rate: isEmployeeDaysMode ? numOrNull(item.calc_daily_rate) : (isEmployeeHoursMode ? numOrNull(item.calc_hourly_rate) : null),
      non_employee_amount: empCount > 0 ? amount / empCount : null,
    }));
    // Both lists persist through the same columns; only the category differs.
    const toHrRow = (category) => (ne) => ({
      non_employee_full_name: ne.full_name,
      non_employee_designation: ne.designation || null,
      non_employee_rate: numOrNull(ne.daily_rate) ?? numOrNull(ne.hourly_rate),
      non_employee_hours: numOrNull(ne.hours_worked),
      non_employee_days: numOrNull(ne.days_worked),
      non_employee_amount: nonEmployeeAmount(ne),
      non_employee_remarks: ne.remarks || null,
      non_employee_category: category,
    });
    const nonEmployeeHrRows = (item.non_employees || []).map(toHrRow(NON_EMPLOYEE));
    const otherEmployeeHrRows = (item.other_employees || []).map(toHrRow(OTHER_EMPLOYEE));

    return {
      // Echoed back so the server updates this line in place rather than
      // deleting and rebuilding it, which destroyed the review figures.
      item_id: item.item_id || null,
      project_id: item.project_id || null,
      material_id: null,
      description: item.description,
      specification: item.specification || null,
      quantity: item.quantity || 1,
      unit_of_measure: item.unit_of_measure || 'Day',
      estimated_unit_rate: item.calculation_mode === 'standard' ? (item.estimated_unit_rate || null) : null,
      estimated_amount: amount,
      vat_percentage: num(item.vat_percentage),
      vat_amount: computeItemVat(item),
      boq_item_id: null,
      boq_component_id: null,
      // no line vendor unless "Require a Vendor?" is on
      vendor_id: formData.require_vendor ? otherAwareId(item.vendor_id) : null,
      vendor_other: formData.require_vendor ? otherAwareText(item.vendor_id, item.vendor_other) : null,
      item_requested_for_user_id: item.item_requested_for_user_id
        ? item.item_requested_for_user_id
        : (item.item_requested_for_name ? OTHER_OPTION : null),
      item_requested_for_name: item.item_requested_for_name || null,
      priority_status: formData.priority_status || 'normal',
      ledger_id: otherAwareId(item.ledger_id),
      ledger_other: otherAwareText(item.ledger_id, item.ledger_other),
      expense_category_id: otherAwareId(item.expense_category_id),
      expense_category_other: otherAwareText(item.expense_category_id, item.expense_category_other),
      calculation_mode: item.calculation_mode,
      calculation_figures_label: computeFiguresLabel(item),
      hr_employees: [...employeeHrRows, ...nonEmployeeHrRows, ...otherEmployeeHrRows],
    };
  });

  const validate = () => {
    // Every row is checked. Rows only ever exist because the user clicked
    // "Add Line" — nothing appends a spare — so a blank row is an unfinished
    // line, not something to skip. Removing a row they no longer want is the
    // delete button's job, and that always leaves at least one behind.
    const checks = [
      { key: 'title', label: 'Title / Purpose', ok: !!formData.title },
      { key: 'requested_department_id', label: 'Department', ok: !!formData.requested_department_id },
    ];
    if (items.length === 0) checks.push({ key: 'items', label: 'At least one line item', ok: false });
    items.forEach((item, idx) => {
      const line = `Line ${idx + 1}`;
      // Both columns are NOT NULL on purchase_request_items, so a line missing
      // either is rejected by the server anyway — flagging it here turns a bare
      // 422 into a red border on the field at fault.
      checks.push({ key: `project_id_${idx}`, label: `${line} project`, ok: !!item.project_id });
      checks.push({ key: `ledger_id_${idx}`, label: `${line} ledger`, ok: !!item.ledger_id });
    });

    const { errors: newErrors, valid, missing } = runChecks(checks);
    // The boxes stop typing at the limit, but a draft saved before the limit
    // existed (or pasted text) can still be longer — the server rejects it.
    const tooLong = [];
    if ((formData.title || '').length > TEXT_LIMIT) { newErrors.title = true; tooLong.push(`Title / Purpose (${formData.title.length})`); }
    items.forEach((item, idx) => {
      if ((item.description || '').length > TEXT_LIMIT) {
        newErrors[`description_${idx}`] = true;
        tooLong.push(`Line ${idx + 1} description (${item.description.length})`);
      }
    });
    setErrors(newErrors);
    if (!valid) showToast(missingFieldsMessage(missing), 'error');
    else if (tooLong.length) showToast(`Too long — keep these to ${TEXT_LIMIT} characters: ${tooLong.join(', ')}.`, 'error');
    return valid && !tooLong.length;
  };

  const handleSaveClick = (status) => {
    if (!validate()) return;
    if (status === 'SUBMITTED') {
      beginSubmitFlow();
    } else {
      setConfirmModal(status);
    }
  };

  const beginSubmitFlow = async () => {
    setSaving(true);
    try {
      const stepRes = await api.getEligibleApprovers('PR_APPROVAL', {
        requested_department_id: formData.requested_department_id || null,
      });
      const data = stepRes?.data || stepRes;
      setFirstStepInfo(data);
      if (data?.approver_mode === 'specific_user') {
        const eligible = data.eligible_users || [];
        setApprovers(eligible);
        setSelectedApproverId(eligible.length > 0 ? (eligible[0].user_id || eligible[0].id) : '');
      } else {
        setApprovers([]);
        setSelectedApproverId('');
      }
      pendingStatusRef.current = 'SUBMITTED';
      setShowApproverModal(true);
    } catch (err) {
      showToast(`Could not start the submission: ${errorText(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const executeAction = async (overrideApproverId = null, intent = null) => {
    if (confirmModal === 'DISCARD') {
      api.clearApiCache();
      onBack?.();
      setConfirmModal(null);
      return;
    }

    // The caller says what it is doing. This used to be inferred as
    // `confirmModal || pendingStatusRef.current`, which let a stale
    // confirmModal outrank the submit intent: a failed save left confirmModal
    // set to 'DRAFT' (the catch block never cleared it), so the NEXT submit
    // resolved to 'DRAFT' and was silently saved as a draft with no workflow
    // started. The explicit argument wins; the old sources remain only as a
    // fallback for callers that have not been given one.
    const status = intent || confirmModal || pendingStatusRef.current;
    setSaving(true);
    try {
      const isSubmitting = status === 'SUBMITTED';
      // `''` is not an override, it is "nothing chosen yet". The old test was
      // `overrideApproverId !== null`, and '' passes that — so an empty string
      // reached the API, failed UUID parsing, and (on an existing draft) left
      // the edits saved with the submission silently lost.
      const rawApprover = overrideApproverId || (
        firstStepInfo?.approver_mode === 'specific_user' ? selectedApproverId : undefined
      );
      const approverId = rawApprover || undefined;

      // Fail here rather than half-way: nothing is written until we know the
      // submission can actually go through.
      if (isSubmitting && firstStepInfo?.approver_mode === 'specific_user' && !approverId) {
        showToast('Select the first approver before submitting.', 'error');
        return;
      }
      const mappedItems = buildMappedItems();

      let res;
      if (editPrId) {
        res = await api.updatePR(editPrId, {
          title: formData.title,
          description: formData.description || null,
          priority_status: formData.priority_status || 'normal',
          required_date: formData.required_date || null,
          expected_due_date: formData.expected_due_date || null,
          // '' clears it server-side; a name sets it.
          requested_for: (formData.requested_for || '').trim(),
          requested_department_id: formData.requested_department_id || null,
          purchase_request_type_id: otherAwareId(formData.purchase_request_type_id),
          purchase_request_type_other: otherAwareText(
            formData.purchase_request_type_id, formData.purchase_request_type_other),
          remarks: formData.remarks || null,
          total_estimated_cost: totalRequisitionValue,
          vat_amount: Number(vatAmount.toFixed(2)),
          adjustments_amount: formData.adjustments_amount || null,
          adjustment_reason: formData.adjustment_reason || null,
          require_vendor: formData.require_vendor,
          items: mappedItems,
        });
        // updatePR only persists the edits — unlike createPR it carries no
        // submit_for_approval flag. Without this, hitting Submit on a saved
        // draft quietly re-saved it and left it sitting in My Drafts.
        if (status === 'RESUBMIT' && editWorkflowInstanceId) {
          // Sends the corrected request back to whoever returned it. The
          // server decides where that is — for a request returned to an
          // approver it is handled as an approve-forward.
          await api.resubmitWorkflow(editWorkflowInstanceId, resubmitComment.trim() || null);
        } else if (isSubmitting) {
          await api.submitWorkflow({
            entity_type: 'purchase_request',
            entity_id: editPrId,
            definition_code: 'PR_APPROVAL',
            first_approver_id: approverId,
          });
        }
      } else {
        const payload = {
          pr_number: formData.pr_number || undefined,
          title: formData.title,
          description: formData.description,
          // '' clears it server-side; a name sets it.
          requested_for: (formData.requested_for || '').trim(),
          requested_department_id: formData.requested_department_id || null,
          purchase_request_type_id: otherAwareId(formData.purchase_request_type_id),
          purchase_request_type_other: otherAwareText(
            formData.purchase_request_type_id, formData.purchase_request_type_other),
          priority_status: formData.priority_status || 'normal',
          required_date: formData.required_date || null,
          expected_due_date: formData.expected_due_date || null,
          remarks: formData.remarks || null,
          items: mappedItems,
          total_estimated_cost: totalRequisitionValue,
          vat_amount: Number(vatAmount.toFixed(2)),
          adjustments_amount: formData.adjustments_amount || null,
          adjustment_reason: formData.adjustment_reason || null,
          require_vendor: formData.require_vendor,
          submit_for_approval: isSubmitting,
          first_approver_id: isSubmitting ? approverId : undefined,
        };
        res = await api.createPR(payload);
      }

      api.clearApiCache();
      if (isSubmitting) {
        setSubmittedPrRef(res?.pr_number || formData.pr_number || '');
        setConfirmModal(null);
        setShowApproverModal(false);
        setShowSuccessModal(true);
      } else {
        showToast(
          editPrId
            ? 'Requisition updated!'
            : `Requisition ${res?.pr_number || ''} saved to Draft!`.replace('  ', ' '),
          'success',
        );
        setConfirmModal(null);
        setShowApproverModal(false);
        if (editPrId) {
          onSaveSuccess?.();
        } else {
          const deptId = department?.id || department?.department_id;
          await resetFormForNewEntry();
          fetchDraftCount(deptId);
          setView('drafts');
        }
      }
    } catch (e) {
      console.error(e);
      showToast(`Could not save this requisition: ${errorText(e)}`, 'error');
      // Cleared on failure too. Left set, it became the `status` of whatever
      // the user tried next.
      setConfirmModal(null);
    } finally {
      setSaving(false);
      pendingStatusRef.current = null;
    }
  };

  const backButton = (
    <button
      type="button"
      onClick={() => { api.clearApiCache(); onBack?.(); }}
      style={{
        padding: '8px 16px', borderRadius: 8, border: '1px solid black', background: '#0b318bff',
        color: '#ffffff', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
      }}
    >
      ← <span style={{ fontWeight: 900 }}>{editPrId ? 'Back to My Requisitions' : 'Back to Department Hub'}</span>
    </button>
  );

  // Suppressed while editing one requisition. In that mode this component is
  // rendered *inside* My Drafts, which already sits under the department hub's
  // own tab row — so drawing them again duplicated the whole nav. They would
  // also be a trap: switching tab mid-edit silently abandons the correction.
  // Closing a correction can change what is in drafts — a resubmitted
  // requisition leaves them — so the badge is recomputed rather than left
  // showing a stale count.
  useEffect(() => {
    if (!editingDraft) fetchDraftCount(formData.requested_department_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDraft]);

  const pillRow = !editPrId && (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <button onClick={() => setView('form')} style={pillStyle(view === 'form')}>New HR Requisition</button>
      <button onClick={() => setView('review')} style={pillStyle(view === 'review')}>Pending HR Review {reviewCount > 0 && <Badge count={reviewCount} />}</button>
      <button onClick={() => setView('drafts')} style={pillStyle(view === 'drafts')}>My Drafts {draftCount > 0 && <Badge count={draftCount} />}</button>
    </div>
  );

  if (view === 'review') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Hidden while a request is open for correction — that editor brings
            its own "Back to My Requisitions". */}
        {!editingDraft && backButton}
        {!editingDraft && pillRow}
        <MyInbox onEditingChange={setEditingDraft} stepNameFilter="Department Head Review" title="Pending HR Review" departmentId={formData.requested_department_id} />
      </div>
    );
  }

  if (view === 'drafts') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Hidden while a returned requisition is open for correction: that
            editor brings its own "Back to My Requisitions", and the hub's back
            bar and tabs would sit above it as a second, competing way out. */}
        {!editingDraft && backButton}
        {!editingDraft && pillRow}
        <MyRequisitions
          title="My Drafts"
          departmentId={formData.requested_department_id}
          draftsOnly
          onEditingChange={setEditingDraft}
        />
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading HR requisition form...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {backButton}
        <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>
          Selected Form Type: <span style={{ color: '#0b318bff', fontWeight: 700 }}>Human Resources &amp; Administration Requisition</span>
        </div>
      </div>

      {pillRow}

      <Card style={{ padding: 24, border: '1px solid black' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#0b318bff' }}>Requisition Header</h3>
        <FormGrid cols={4}>
          {/* Empty until the server issues one — see NewRequisition. */}
          <div>
            <Input label="Requisition Ref ID" value={formData.pr_number} placeholder="Will be generated on submission" disabled style={{ fontWeight: 'bold', color: '#0b318bff', border: '1px solid black' }} />
            {!formData.pr_number && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                Generated automatically when you submit or save.
              </div>
            )}
          </div>
          <Input
            label="Title / Purpose"
            placeholder="e.g. Casual labour payment for site clean-up"
            value={formData.title}
            maxLength={TEXT_LIMIT}
            style={{ border: errors.title && (!formData.title || formData.title.length > TEXT_LIMIT) ? '2px solid #ef4444' : undefined }}
            onChange={(v) => setFormData({ ...formData, title: v })}
          />
          <Input label="Requester" disabled value={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : ''} style={{ fontWeight: 'bold', color: '#0b318bff', border: '1px solid black' }} />
          <RequestedForField
            value={formData.requested_for}
            onChange={(v) => setFormData({ ...formData, requested_for: v })}
          />
          <Select
            label="Department"
            required
            options={departments.map((d) => ({ value: d.id || d.department_id, label: d.name }))}
            value={formData.requested_department_id}
            onChange={(v) => setFormData({ ...formData, requested_department_id: v })}
            style={{ border: errors.requested_department_id && !formData.requested_department_id ? '2px solid #ef4444' : undefined }}
          />

          <Select
            label="Request Type"
            options={withOtherOption(purchaseRequestTypes.map((t) => ({ value: t.id || t.type_id, label: t.name })))}
            value={formData.purchase_request_type_id}
            onChange={(v) => setFormData({ ...formData, purchase_request_type_id: v })}
          />
          {formData.purchase_request_type_id === OTHER_OPTION && (
            <Input
              label="Specify Request Type"
              value={formData.purchase_request_type_other}
              onChange={(v) => setFormData({ ...formData, purchase_request_type_other: v })}
              placeholder="Type the request type"
              required
            />
          )}
          <Select
            label="Priority"
            options={[
              { value: 'low', label: 'Low' },
              { value: 'normal', label: 'Normal' },
              { value: 'high', label: 'High' },
              { value: 'urgent', label: 'Urgent' },
              { value: 'critical', label: 'Critical' },
            ]}
            value={formData.priority_status}
            onChange={(v) => setFormData({ ...formData, priority_status: v })}
          />
          <DatePicker label="Required Date" value={formData.required_date} onChange={(v) => setFormData({ ...formData, required_date: v })} />
          <DatePicker label="Expected Due Date" value={formData.expected_due_date} onChange={(v) => setFormData({ ...formData, expected_due_date: v })} />

          {/* Forced to column 1 so the two text areas always start a fresh
              row and stay side by side. The single-width fields above them
              are an odd count, so left to flow, Purpose slid up beside
              Expected Due Date and pushed Remarks onto a row of its own. */}
          <div style={{ gridColumn: '1 / span 2' }}>
            <Textarea label="Purpose / Justification" rows={2} placeholder="Brief purpose or justification" value={formData.description} onChange={(v) => setFormData({ ...formData, description: v })} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Textarea label="Remarks" rows={2} placeholder="Any additional remarks..." value={formData.remarks} onChange={(v) => setFormData({ ...formData, remarks: v })} />
          </div>
        </FormGrid>
      </Card>

      <Card style={{ padding: 0, border: '1px solid black', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid black', background: '#f8fafc' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0b318bff', margin: 0 }}>Line Items — HR Payment Grid</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{items.length} line(s) · Total: ₦{fmt(grossAmount)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none', fontSize: 12.5, fontWeight: 600, color: formData.require_vendor ? '#0b318b' : '#64748b' }}>
              <div
                onClick={() => setFormData(prev => {
                  const turningOff = prev.require_vendor;
                  return {
                    ...prev,
                    require_vendor: !prev.require_vendor,
                    ...(turningOff && {
                      items: items.map(it => ({ ...it, vendor_id: '', vendor_other: '' })),
                    }),
                  };
                })}
                style={{ width: 36, height: 20, borderRadius: 10, background: formData.require_vendor ? '#0b318b' : '#cbd5e1', position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0 }}
              >
                <div style={{ position: 'absolute', top: 2, left: formData.require_vendor ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </div>
              Require a Vendor?
            </label>
            <Btn onClick={addItem} style={{ background: '#0b318bff', color: '#fff', border: 'none' }}>Add Line</Btn>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 1900 }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff' }}>
                <th style={{ padding: '10px' }}>#</th>
                <th style={{ padding: '10px', textAlign: 'center', minWidth: 160 }}>LEDGER</th>
                <th style={{ padding: '10px', textAlign: 'center', minWidth: 180 }}>EXPENSE CATEGORY</th>
                <th style={{ padding: '10px', textAlign: 'center', minWidth: 180 }}>DESCRIPTION</th>
                <th style={{ padding: '10px', textAlign: 'center', minWidth: 160 }}>SPECIFICATION</th>
                <th style={{ padding: '10px', minWidth: 100 }}>EMPLOYEE</th>
                <th style={{ padding: '10px', minWidth: 100 }}>OTHER EMPLOYEES</th>
                <th style={{ padding: '10px', minWidth: 100 }}>NON-EMPLOYEE</th>
                <th style={{ padding: '10px', minWidth: 130 }}>DEPARTMENT</th>
                <th style={{ padding: '10px', minWidth: 160 }}>REQUESTED FOR</th>
                {formData.require_vendor && <th style={{ padding: '10px', minWidth: 150 }}>VENDOR</th>}
                <th style={{ padding: '10px', minWidth: 150 }}>PROJECT SITE</th>
                <th style={{ padding: '10px', minWidth: 90 }}>QTY</th>
                <th style={{ padding: '10px', minWidth: 100 }}>UOM</th>
                <th style={{ padding: '10px', minWidth: 110 }}>UNIT PRICE</th>
                <th style={{ padding: '10px', minWidth: 120 }}>AMOUNT</th>
                <th style={{ padding: '10px', minWidth: 90 }}>VAT (%)</th>
                <th style={{ padding: '10px', minWidth: 100 }}>MORE CALCULATION</th>
                <th style={{ padding: '10px', minWidth: 120 }}>REMARKS</th>
                <th style={{ padding: '10px' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const amount = computeItemAmount(item);
                const isStandard = item.calculation_mode === 'standard';
                return (
                  <React.Fragment key={idx}>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 8, textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ padding: 8 }}>
                        <Select
                          value={item.ledger_id}
                          onChange={(v) => updateItem(idx, { ledger_id: v })}
                          style={errors[`ledger_id_${idx}`] && !item.ledger_id ? { border: '1.5px solid #ef4444' } : undefined}
                          options={withOtherOption([{ value: '', label: '— Select Ledger —' }, ...ledgers.map((l) => ({ value: l.id, label: l.name }))])}
                        />
                        {/* Under the picker rather than in its own column, so
                            choosing Others does not shift the rest of the row. */}
                        {item.ledger_id === OTHER_OPTION && (
                          <input
                            style={{ width: '100%', padding: 8, marginTop: 6, border: '1px solid var(--border-light)', borderRadius: 6 }}
                            placeholder="Specify ledger *"
                            value={item.ledger_other || ''}
                            onChange={(e) => updateItem(idx, { ledger_other: e.target.value })}
                          />
                        )}
                      </td>
                      <td style={{ padding: 8 }}>
                        <Select
                          value={item.expense_category_id || ''}
                          onChange={(v) => updateItem(idx, { expense_category_id: v })}
                          options={withOtherOption([
                            { value: '', label: '— Select Expense Category —' },
                            ...expenseCategories.map((e2) => ({
                              value: e2.id || e2.expense_category_id,
                              label: `${e2.code} · ${e2.name}`,
                            })),
                          ])}
                        />
                        {item.expense_category_id === OTHER_OPTION && (
                          <input
                            style={{ width: '100%', padding: 8, marginTop: 6, border: '1px solid var(--border-light)', borderRadius: 6 }}
                            placeholder="Specify category *"
                            value={item.expense_category_other || ''}
                            onChange={(e) => updateItem(idx, { expense_category_other: e.target.value })}
                          />
                        )}
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          style={{
                            width: '100%', padding: 8, borderRadius: 6,
                            border: (errors[`description_${idx}`] && (!item.description || item.description.length > TEXT_LIMIT))
                              ? '1.5px solid #ef4444' : '1px solid var(--border-light)',
                          }}
                          placeholder="Description"
                          maxLength={TEXT_LIMIT}
                          value={item.description}
                          onChange={(e) => updateItem(idx, { description: e.target.value })}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border-light)' }}
                          placeholder="Specification"
                          value={item.specification || ''}
                          onChange={(e) => updateItem(idx, { specification: e.target.value })}
                        />
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <Btn variant="outline" size="sm" onClick={() => setEmployeeSelectorForLine(idx)}>
                          {item.employee_user_ids.length > 0 ? `${item.employee_user_ids.length} Employee(s)` : 'Select'}
                        </Btn>
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <Btn variant="outline" size="sm" onClick={() => setOtherEmployeeModalForLine(idx)}>
                          {(item.other_employees || []).length > 0 ? `${item.other_employees.length} record(s)` : 'Add'}
                        </Btn>
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <Btn variant="outline" size="sm" onClick={() => setNonEmployeeModalForLine(idx)}>
                          {item.non_employees.length > 0 ? `${item.non_employees.length} record(s)` : 'Add'}
                        </Btn>
                      </td>
                      <td style={{ padding: 8 }}>
                        <Select
                          value={formData.requested_department_id}
                          onChange={() => {}}
                          disabled
                          options={departments.map((d) => ({ value: d.id || d.department_id, label: d.name }))}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        {(() => {
                          return (
                            <>
                              <Select
                                value={item.item_requested_for_user_id || ''}
                                onChange={(v) => updateItem(idx, {
                                  item_requested_for_user_id: v || null,
                                  item_requested_for_name: v === OTHER_OPTION ? (item.item_requested_for_name || '') : null,
                                })}
                                options={[
                                  { value: '', label: '— None —' },
                                  { value: OTHER_OPTION, label: 'Other (type name)' },
                                  ...users.map((u) => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` })),
                                ]}
                                placeholder="— None —"
                              />
                              {item.item_requested_for_user_id === OTHER_OPTION && (
                                <input
                                  style={{ width: '100%', boxSizing: 'border-box', padding: 8, marginTop: 6, border: '1px solid var(--border-light)', borderRadius: 6, fontSize: 12.5 }}
                                  placeholder="Type full name *"
                                  value={item.item_requested_for_name || ''}
                                  onChange={(e) => updateItem(idx, { item_requested_for_name: e.target.value })}
                                />
                              )}
                            </>
                          );
                        })()}
                      </td>
                      {formData.require_vendor && (
                        <td style={{ padding: 8 }}>
                          <Select
                            value={item.vendor_id || ''}
                            onChange={(v) => updateItem(idx, { vendor_id: v })}
                            options={withOtherOption(suppliers.map((sup) => ({
                              value: sup.id || sup.supplier_id, label: sup.name,
                            })))}
                            placeholder="Select"
                          />
                          {item.vendor_id === OTHER_OPTION && (
                            <input
                              style={{ width: '100%', boxSizing: 'border-box', padding: 8, marginTop: 6,
                                       border: '1px solid var(--border-light)', borderRadius: 6, fontSize: 12.5 }}
                              placeholder="Specify vendor *"
                              value={item.vendor_other || ''}
                              onChange={(e) => updateItem(idx, { vendor_other: e.target.value })}
                            />
                          )}
                        </td>
                      )}
                      <td style={{ padding: 8 }}>
                        <Select
                          value={item.project_id}
                          onChange={(v) => updateItem(idx, { project_id: v })}
                          style={errors[`project_id_${idx}`] && !item.project_id ? { border: '1.5px solid #ef4444' } : undefined}
                          options={[{ value: '', label: '—' }, ...projects.map((p) => ({ value: p.id || p.project_id, label: p.project_name || p.name }))]}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <input type="number" style={{ width: 70, padding: 8 }} value={item.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
                      </td>
                      <td style={{ padding: 8 }}>
                        <Select value={item.unit_of_measure} onChange={(v) => updateItem(idx, { unit_of_measure: v })} options={UOM_OPTIONS} />
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          type="number"
                          disabled={!isStandard}
                          style={{ width: 90, padding: 8, opacity: isStandard ? 1 : 0.5 }}
                          value={item.estimated_unit_rate}
                          onChange={(e) => updateItem(idx, { estimated_unit_rate: e.target.value })}
                        />
                      </td>
                      <td style={{ padding: 8, textAlign: 'center', fontWeight: 800 }}>₦{fmt(amount)}</td>
                      {/* Per-line VAT: 0 by default, because a requisition can
                          carry taxable and non-taxable lines side by side. */}
                      <td style={{ padding: 8 }}>
                        <input
                          type="number" min="0" max="100" step="0.5"
                          style={{ width: 70, padding: 8 }}
                          value={item.vat_percentage ?? 0}
                          onChange={(e) => updateItem(idx, { vat_percentage: e.target.value })}
                        />
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <Btn variant="outline" size="sm" onClick={() => updateItem(idx, { showCalc: !item.showCalc })}>
                          {item.showCalc ? 'Hide' : 'Calc'}
                        </Btn>
                      </td>
                      <td style={{ padding: 8 }}>
                        <input style={{ width: '100%', padding: 8 }} placeholder="Remarks" value={item.remarks} onChange={(e) => updateItem(idx, { remarks: e.target.value })} />
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        {items.length > 1 && (
                          <RemoveLineButton onClick={() => removeItem(idx)} />
                        )}
                      </td>
                    </tr>

                    {item.showCalc && (
                      <tr>
                        <td colSpan={17} style={{ padding: 0, background: '#fffbea', borderBottom: '2px solid #eab308' }}>
                          <div style={{ padding: 16 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#92400e', marginBottom: 10, textTransform: 'uppercase' }}>
                              More Calculations — Intelligent Calculation Panel
                            </div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                              <div style={{ minWidth: 260 }}>
                                <label style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'block', marginBottom: 4 }}>CALCULATION MODE</label>
                                <Select
                                  value={item.calculation_mode}
                                  onChange={(v) => updateItem(idx, { calculation_mode: v })}
                                  options={CALC_MODES}
                                />
                              </div>

                              {item.calculation_mode === 'employees_days_daily' && (
                                <>
                                  <NumField label="Days" value={item.calc_days} onChange={(v) => updateItem(idx, { calc_days: v })} />
                                  <NumField label="Daily Rate (₦)" value={item.calc_daily_rate} onChange={(v) => updateItem(idx, { calc_daily_rate: v })} />
                                </>
                              )}
                              {item.calculation_mode === 'employees_hours_hourly' && (
                                <>
                                  <NumField label="Hours" value={item.calc_hours} onChange={(v) => updateItem(idx, { calc_hours: v })} />
                                  <NumField label="Hourly Rate (₦)" value={item.calc_hourly_rate} onChange={(v) => updateItem(idx, { calc_hourly_rate: v })} />
                                </>
                              )}
                              {item.calculation_mode === 'fixed_amount' && (
                                <NumField label="Fixed Amount (₦)" value={item.calc_fixed_amount} onChange={(v) => updateItem(idx, { calc_fixed_amount: v })} />
                              )}
                              {item.calculation_mode === 'bulk_equal_split' && (
                                <NumField label="Total Amount to Split (₦)" value={item.calc_bulk_total} onChange={(v) => updateItem(idx, { calc_bulk_total: v })} />
                              )}
                              {item.calculation_mode === 'custom_amount' && (
                                <NumField label="Custom Amount (₦)" value={item.calc_custom_amount} onChange={(v) => updateItem(idx, { calc_custom_amount: v })} />
                              )}

                              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', padding: '8px 14px', background: '#fff', border: '1px solid #eab308', borderRadius: 6 }}>
                                {computeFiguresLabel(item)} = ₦{fmt(amount)}
                              </div>
                            </div>
                            {item.calculation_mode === 'bulk_equal_split' && item.employee_user_ids.length > 0 && (
                              <div style={{ marginTop: 8, fontSize: 12, color: '#92400e' }}>
                                Each of the {item.employee_user_ids.length} selected employee(s) receives ₦{fmt(amount / item.employee_user_ids.length)}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, padding: 20, borderTop: '1px solid black' }}>
          <Stat label="Line Items" value={items.length} />
          <Stat label="Employees" value={employeeCount} />
          <Stat label="Non-Employees" value={nonEmployeeCount} />
          <Stat label="Other Employees" value={otherEmployeeCount} />
          <Stat label="Total Days" value={totalDays} />
          <Stat label="Total Hours" value={totalHours} />
          <Stat label="Gross Amount" value={`₦${fmt(grossAmount)}`} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 24, padding: '0 20px 20px' }}>
          <div style={{ minWidth: 220 }}>
            <Input label="Adjustments (₦)" type="number" value={formData.adjustments_amount} onChange={(v) => setFormData({ ...formData, adjustments_amount: v })} />
          </div>
          <div style={{ minWidth: 260 }}>
            <Input label="Adjustment Reason" value={formData.adjustment_reason} onChange={(v) => setFormData({ ...formData, adjustment_reason: v })} />
          </div>
          <div style={{ textAlign: 'right' }}>
            {/* Spelled out so an approver can see what the total is made of
                rather than having to add the lines back up. */}
            <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 4 }}>
              Sub-total ₦{fmt(grossAmount)} · VAT ₦{fmt(vatAmount)}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>TOTAL REQUISITION VALUE</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#0b318bff' }}>₦{fmt(totalRequisitionValue)}</div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
        <button
          onClick={() => setConfirmModal('DISCARD')}
          disabled={saving}
          style={{
            background: '#ef4444', color: '#fff', border: 'none', padding: '12px 24px',
            borderRadius: 8, fontWeight: 900,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => handleSaveClick('DRAFT')}
          disabled={saving}
          style={{ background: '#eab308', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          {editPrId ? 'Save Changes' : 'Save Draft'}
        </button>
        {editPrId && editPrStatus === 'returned' && (
          <button
            onClick={() => handleSaveClick('RESUBMIT')}
            disabled={saving}
            style={{
              background: '#16a34a', color: '#fff', border: 'none', padding: '12px 24px',
              borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, opacity: saving ? 0.85 : 1,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            {saving ? 'Resubmitting…' : 'Save & Resubmit'}
          </button>
        )}
        {!editPrId && (
          <button
            onClick={() => handleSaveClick('SUBMITTED')}
            disabled={saving}
            style={{
              background: '#16a34a', color: '#fff', border: 'none', padding: '12px 24px',
              borderRadius: 8, fontWeight: 900, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, opacity: saving ? 0.85 : 1,
            }}
          >
            {saving && <Spinner size={15} />}
            {saving ? 'Submitting…' : (skipsDepartmentReview ? 'Submit to Cost Control' : 'Submit to HR Review')}
          </button>
        )}
      </div>

      {employeeSelectorForLine !== null && (
        <EmployeeSelectorModal
          users={users}
          departments={departments}
          initialSelectedIds={items[employeeSelectorForLine].employee_user_ids}
          onCancel={() => setEmployeeSelectorForLine(null)}
          onApply={(ids) => { updateItem(employeeSelectorForLine, { employee_user_ids: ids }); setEmployeeSelectorForLine(null); }}
        />
      )}

      {nonEmployeeModalForLine !== null && (
        <NonEmployeeModal
          title="Non-Employee Payment Details"
          hint="Add casual / contract / external personnel not registered as staff."
          saveLabel="Save Non-Employee Details"
          initialRows={items[nonEmployeeModalForLine].non_employees}
          onCancel={() => setNonEmployeeModalForLine(null)}
          onSave={(rows) => { updateItem(nonEmployeeModalForLine, { non_employees: rows }); setNonEmployeeModalForLine(null); }}
        />
      )}

      {otherEmployeeModalForLine !== null && (
        <NonEmployeeModal
          title="Other Employees Payment Details"
          hint="Add employees being paid on this requisition who are not on the employee list above."
          saveLabel="Save Other Employees"
          initialRows={items[otherEmployeeModalForLine].other_employees || []}
          onCancel={() => setOtherEmployeeModalForLine(null)}
          onSave={(rows) => { updateItem(otherEmployeeModalForLine, { other_employees: rows }); setOtherEmployeeModalForLine(null); }}
        />
      )}

      {/* Mirrors the inbox's Authorize Request dialog — same width, same
          layout, same wording — because it is the same action underneath:
          resubmit delegates to approve when a request was returned to its
          holder for correction. Two dialogs for one action is what made the
          three routes look like three different things. */}
      {confirmModal === 'RESUBMIT' && (
        <Modal title="Authorize Request" onClose={saving ? () => { } : () => setConfirmModal(null)}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              You are about to <strong>save and resubmit</strong> the purchase request
              <strong> {formData.pr_number}</strong>.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                Comment
              </label>
              <Input
                value={resubmitComment}
                onChange={setResubmitComment}
                placeholder="Optional comment..."
                as="textarea"
                rows={3}
                disabled={saving}
              />
            </div>

            {resubmitTarget && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Goes back to - {resubmitTarget}
                </label>
                <ReturnDestinationNote stepName={resubmitTarget} />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" disabled={saving} onClick={() => setConfirmModal(null)}>Cancel</Btn>
            <Btn
              onClick={() => executeAction(null, confirmModal)}
              loading={saving}
              style={{ background: '#16a34a', color: '#fff', border: 'none' }}
            >
              {saving ? 'Resubmitting…' : 'Confirm Resubmit'}
            </Btn>
          </div>
        </Modal>
      )}

      {confirmModal && confirmModal !== 'RESUBMIT' && (
        <Modal title="Confirm Action" onClose={saving ? () => { } : () => setConfirmModal(null)} width={400}>
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ fontSize: 15, color: '#475569', marginBottom: 24 }}>
              {confirmModal === 'DISCARD' && 'Are you sure you want to discard all changes? This action cannot be undone.'}
              {confirmModal === 'DRAFT' && 'Are you sure you want to save this requisition as a draft? You can submit it later.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
              <Btn variant="outline" onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn onClick={() => executeAction(null, confirmModal)} disabled={saving} style={{ background: confirmModal === 'DISCARD' ? '#ef4444' : (confirmModal === 'RESUBMIT' ? '#16a34a' : '#eab308'), color: '#fff', border: 'none' }}>
                {saving ? 'Processing...' : 'Confirm'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {showApproverModal && (
        <Modal title="Submit for Approval"
          onClose={saving ? () => { } : () => { pendingStatusRef.current = null; setShowApproverModal(false); }}>
          <div style={{ padding: 16 }}>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#475569' }}>
              This requisition will be sent for approval, starting with the step below.
            </p>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
              First Approver{firstStepInfo?.step_name ? ` - ${firstStepInfo.step_name}` : ''}
            </label>
            {firstStepInfo?.approver_mode === 'specific_user' ? (
              <Select
                value={selectedApproverId}
                onChange={setSelectedApproverId}
                options={approvers.map((a) => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
              />
            ) : (
              <Select value="step_queue" onChange={() => {}} options={[{ value: 'step_queue', label: `🏢 ${firstStepInfo?.step_name || 'Approval Step'}` }]} />
            )}
          </div>
          <SlowRequestHint active={saving} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <Btn variant="outline" disabled={saving} onClick={() => { pendingStatusRef.current = null; setShowApproverModal(false); }}>Cancel</Btn>
            <Btn onClick={() => executeAction(selectedApproverId, 'SUBMITTED')} loading={saving} disabled={saving} style={{ background: '#16a34a', color: '#fff', border: 'none' }}>
              {saving ? 'Submitting…' : 'Submit PR'}
            </Btn>
          </div>
        </Modal>
      )}

      {showSuccessModal && (
        <Modal hideHeader width={420} onClose={handleSuccessModalClose}>
          <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: '0 0 0 8px #f0fdf4' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Requisition Submitted!</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
              Requisition <strong style={{ color: '#0b318bff' }}>{submittedPrRef}</strong> has been created and successfully submitted to {firstStepInfo?.step_name || 'the first approval step'} for processing.
            </p>
            <button
              onClick={handleSuccessModalClose}
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#fff', border: 'none', padding: '10px 32px', borderRadius: 20, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              Great, Thanks!
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function NumField({ label, value, onChange }) {
  return (
    <div style={{ minWidth: 140 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'block', marginBottom: 4 }}>{label}</label>
      <input type="number" style={{ width: '100%', padding: 8, border: '1px solid var(--border-light)', borderRadius: 6 }} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{value}</div>
    </div>
  );
}

export function pillStyle(active) {
  return {
    padding: '10px 20px',
    borderRadius: 10,
    border: active ? '2px solid #0b318bff' : '1px solid black',
    background: active ? '#0b318bff' : '#fff',
    color: active ? '#fff' : '#0f172a',
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
}

export function Badge({ count }) {
  return (
    <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 800 }}>
      {count}
    </span>
  );
}
